import { EDocumentStatus, EDocumentType, EPaymentMethod, getEPaymentMethodList } from '@if/core/types'
import { ControlledDebouncedNumberField, ControlledSelect, useTranslatedOptionEnum } from '@if/mui'
import { Delete } from '@mui/icons-material'
import InfoOutlineIcon from '@mui/icons-material/InfoOutline'
import { Box, Button, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { IconButtonWithTooltip } from '@src/components'
import SelectMerchantDialog from '@src/components/SelectMerchantDialog'
import { WORKSHEET_INPUT_DEBOUNCE_MS } from '@src/components/WorksheetForm'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetMerchantsByIds } from '@src/data/api/merchant/merchant-api'
import { useCompleteFinalizeHighEstimate } from '@src/data/api/personal-loan-worksheet/personal-loan-worksheet-api'
import { useFeatureFlags, useSetBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { useHasCVT } from '@src/data/selectors/credit-selectors'
import { EPaymentMethodMerchants, InternalMerchants, Merchant } from '@src/data/types'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { extractWorksheetSaveErrors } from '@src/services/worksheet-save-error'
import { sumBy } from 'lodash-es'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Control, FieldPath, useFieldArray, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PersonalEditValues } from '../personalEditForm'

export const PAY_CUSTOMER_IDS: string[] = [
  EPaymentMethodMerchants.PayCustomerByBankTransfer,
  EPaymentMethodMerchants.PayCustomerByCheque,
]

const isBorrowerRow = (merchantId: string): boolean => PAY_CUSTOMER_IDS.includes(merchantId)

interface PaymentSplitSectionProps {
  // Form access is passed in, like every other worksheet field component, so the section stays
  // renderable from a plain useForm harness in tests.
  control: Control<PersonalEditValues>
  merchant: Merchant
  isVariableInterest: boolean
}

// The borrower/merchant payment split: add borrower / add merchant actions, one controlled row
// per payment, and the high-estimate finalize action.
const PaymentSplitSection = ({ control, merchant, isVariableInterest }: PaymentSplitSectionProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const featureFlags = useFeatureFlags()
  const setBusinessErrors = useSetBusinessErrors()
  const { selectedCompany } = usePreferences()
  const hasCVT = useHasCVT()

  const { creditApplication, worksheet, documents } = useCreditApplicationData()
  const { fields, append, remove, replace, update } = useFieldArray({ control, name: 'merchantPayments' })

  // `fields` is a render-stable snapshot; live values come from the watch.
  const watchedPayments = useWatch({ control, name: 'merchantPayments' })
  const payments = useMemo(() => watchedPayments ?? [], [watchedPayments])
  const isHighEstimate = useWatch({ control, name: 'isHighEstimate' })
  const amountRequested = useWatch({ control, name: 'amountRequested' }) ?? 0

  const [isMerchantDialogOpen, setIsMerchantDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dialogSelectedMerchants, setDialogSelectedMerchants] = useState<Record<string, Merchant>>({})
  const [completeFinalizeHighEstimate] = useCompleteFinalizeHighEstimate()

  const merchantIdsToFetch = useMemo(() => {
    const removedMerchantIds = [...PAY_CUSTOMER_IDS, merchant.id]
    return payments.filter((p) => !removedMerchantIds.includes(p.merchantId)).map((p) => p.merchantId)
  }, [payments, merchant])

  const [fetchedMerchants, isLoadingMerchants] = useGetMerchantsByIds(merchantIdsToFetch)

  const paymentMerchantsByIds = useMemo(() => {
    const map: Record<string, Merchant> = { [merchant.id]: merchant }
    fetchedMerchants.forEach((m) => {
      map[m.id] = m
    })
    Object.values(dialogSelectedMerchants).forEach((m) => {
      map[m.id] = m
    })
    return map
  }, [merchant, fetchedMerchants, dialogSelectedMerchants])

  // A promo plan is financed by the merchant, so paying the borrower directly is not allowed —
  // unless the buy-down feature switch lifts the restriction and the merchant may pay borrowers.
  const borrowerPaymentsAllowedOnPromo = featureFlags.useHighEstimateOnBuyDown && merchant.canPayBorrowers
  useEffect(() => {
    if (!isVariableInterest || borrowerPaymentsAllowedOnPromo) return
    if (!payments.some((p) => isBorrowerRow(p.merchantId))) return
    replace(payments.filter((p) => !isBorrowerRow(p.merchantId)))
  }, [isVariableInterest, borrowerPaymentsAllowedOnPromo, payments, replace])

  // "Complete High Estimate" is only available once the required merchant invoice has been
  // created, uploaded and approved.
  const isHighEstimateInvoiceApproved = documents.some(
    (doc) => doc.typeId === EDocumentType.ClinicInvoice && doc.status === EDocumentStatus.Approved,
  )

  const borrowerMethods = useMemo(
    () => getEPaymentMethodList().filter((m) => m !== EPaymentMethod.Wire && m !== EPaymentMethod.DEFT),
    [],
  )
  const merchantMethods = useMemo(() => getEPaymentMethodList().filter((m) => m !== EPaymentMethod.BankTransfer), [])
  const borrowerMethodOptions = useTranslatedOptionEnum('EPaymentMethod', borrowerMethods, 'enum')
  const merchantMethodOptions = useTranslatedOptionEnum('EPaymentMethod', merchantMethods, 'enum')

  const getServiceProviderTooltip = useCallback((): ReactNode => {
    if (!featureFlags.usePersonalLoanServiceProviderName) return ''
    const isUnregisteredMerchantNameMissing =
      !creditApplication?.unregisteredMerchantName || creditApplication.unregisteredMerchantName.trim() === ''
    const isStandardMerchant = InternalMerchants.includes(merchant.id)
    const title = isUnregisteredMerchantNameMissing ? (
      t('editCreditApplication.unregisteredMerchantName.missing')
    ) : (
      <>
        {`${t('editCreditApplication.unregisteredMerchantName.label')} :`}
        <br />
        {creditApplication?.unregisteredMerchantName}
      </>
    )
    return isStandardMerchant || !isUnregisteredMerchantNameMissing ? (
      <Tooltip title={title} placement="top">
        <InfoOutlineIcon color={isStandardMerchant && isUnregisteredMerchantNameMissing ? 'warning' : 'inherit'} />
      </Tooltip>
    ) : (
      ''
    )
  }, [creditApplication, merchant, featureFlags.usePersonalLoanServiceProviderName, t])

  const getMerchantName = useCallback(
    (merchantId: string): ReactNode => {
      if (creditApplication && isBorrowerRow(merchantId)) {
        return (
          <>
            {creditApplication.applicant.firstName} {creditApplication.applicant.lastName} {getServiceProviderTooltip()}
          </>
        )
      }
      return paymentMerchantsByIds[merchantId]?.name ?? ''
    },
    [paymentMerchantsByIds, getServiceProviderTooltip, creditApplication],
  )

  const handleMerchantsSelected = useCallback(
    (newMerchants: Merchant[]) => {
      setDialogSelectedMerchants((old) => {
        const updated = { ...old }
        newMerchants.forEach((m) => {
          updated[m.id] = m
        })
        return updated
      })
      newMerchants
        .filter((merch) => !payments.some((p) => p.merchantId === merch.id))
        .forEach((merch) =>
          append({
            merchantId: merch.id,
            amount: 0,
            paymentMethod: merch.defaultPaymentMethod ?? EPaymentMethod.Check,
          }),
        )
      setIsMerchantDialogOpen(false)
    },
    [payments, append],
  )

  const addBorrower = () => {
    if (payments.some((p) => isBorrowerRow(p.merchantId))) return
    append({
      merchantId: EPaymentMethodMerchants.PayCustomerByBankTransfer,
      amount: 0,
      paymentMethod: EPaymentMethod.BankTransfer,
    })
  }

  // A borrower row encodes its payout channel in the merchant id, so the id follows the method.
  const syncBorrowerMerchantId = (index: number, method: EPaymentMethod) => {
    const payment = payments[index]
    if (!payment || !isBorrowerRow(payment.merchantId)) return
    update(index, {
      ...payment,
      paymentMethod: method,
      merchantId:
        method === EPaymentMethod.BankTransfer
          ? EPaymentMethodMerchants.PayCustomerByBankTransfer
          : EPaymentMethodMerchants.PayCustomerByCheque,
    })
  }

  const isPaymentMethodDisabled = (merchantId: string, paymentMethod: EPaymentMethod) => {
    // disabled for the time being. ALIS does not support custom merchant methods.
    // remove this disabling function once ALIS has support.
    if (isBorrowerRow(merchantId)) return false
    return paymentMethod === paymentMerchantsByIds[merchantId]?.defaultPaymentMethod
  }

  const finalizeHighEstimate = async () => {
    setIsSubmitting(true)
    try {
      const totalPayments = sumBy(payments, 'amount')
      if (totalPayments !== amountRequested) {
        setBusinessErrors([{ message: 'worksheetCommon.merchantPaymentTotalMismatch' }])
      }
      if (!worksheet?.isHighEstimate) setBusinessErrors([{ message: 'worksheetCommon.notHighEstimate' }])
      if (!hasCVT) setBusinessErrors([{ message: 'worksheetCommon.optionNotAvailable' }])
      if (!isHighEstimateInvoiceApproved) {
        setBusinessErrors([{ message: 'worksheetCommon.invoiceNotApproved' }])
      } else {
        await completeFinalizeHighEstimate({
          dto: { merchantPayments: payments, creditApplicationId: creditApplication!.id },
          creditApplicationId: creditApplication!.id,
        })
        navigate(`/Applications/${creditApplication!.financingProgramId}/${creditApplication!.id}/view`, {
          replace: true,
        })
      }
    } catch (error) {
      reportErrorToConsole(error)
      setBusinessErrors(extractWorksheetSaveErrors(error, 'worksheet.saveFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Grid size={12}>
      <Box
        width="100%"
        sx={{ border: 1, padding: 2, marginTop: 2, borderColor: theme.palette.divider, borderRadius: 1 }}
      >
        <Grid container marginBottom={4} justifyContent="flex-end" alignItems="center" display="flex">
          <Grid>
            <Stack direction="row" spacing={1}>
              <Button
                color="secondary"
                variant="contained"
                onClick={addBorrower}
                disabled={
                  payments.some((p) => isBorrowerRow(p.merchantId)) ||
                  !merchant.canPayBorrowers ||
                  merchant.forcePaymentToBorrower ||
                  (isVariableInterest && !borrowerPaymentsAllowedOnPromo)
                }
              >
                {t('worksheetCommon.addBorrower')}
              </Button>
              <Button
                color="secondary"
                variant="contained"
                disabled={merchant.forcePaymentToBorrower}
                onClick={() => setIsMerchantDialogOpen(true)}
              >
                {t('worksheetCommon.addMerchantPayment')}
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {!isLoadingMerchants &&
          fields.map((field, index) => {
            const payment = payments[index]
            if (!payment) return null
            const borrower = isBorrowerRow(payment.merchantId)
            return (
              <Stack key={field.id} direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                <Typography sx={{ width: '100%', marginTop: '1rem' }}>{getMerchantName(payment.merchantId)}</Typography>
                <ControlledSelect
                  control={control}
                  name={`merchantPayments.${index}.paymentMethod` as FieldPath<PersonalEditValues>}
                  label={t('worksheetCommon.paymentMethod')}
                  options={borrower ? borrowerMethodOptions : merchantMethodOptions}
                  onChange={(value: EPaymentMethod) => syncBorrowerMerchantId(index, value)}
                  disabled={isPaymentMethodDisabled(payment.merchantId, payment.paymentMethod)}
                  disableEmptyOption
                  fullWidth
                />
                <ControlledDebouncedNumberField
                  control={control}
                  name={`merchantPayments.${index}.amount` as FieldPath<PersonalEditValues>}
                  label={t('worksheetCommon.paymentAmount')}
                  debounceTime={WORKSHEET_INPUT_DEBOUNCE_MS}
                  isMoney
                  sx={{ width: '30rem' }}
                />
                <div style={{ marginTop: '1%' }}>
                  <IconButtonWithTooltip
                    disabled={isVariableInterest && payment.merchantId === merchant.id}
                    onClick={() => remove(index)}
                    tooltip="delete"
                    color="primary"
                    icon={<Delete />}
                  />
                </div>
              </Stack>
            )
          })}

        {merchant.canAcceptHighEstimate && isHighEstimate && (
          <Grid container justifyContent="center" mt={4}>
            <Grid>
              <Button
                color="success"
                variant="contained"
                disabled={!isHighEstimateInvoiceApproved || isSubmitting}
                onClick={() => void finalizeHighEstimate()}
              >
                {t('worksheetCommon.finalizeHighEstimateWorksheet')}
              </Button>
              {!isHighEstimateInvoiceApproved && (
                <Typography variant="caption" component="div" sx={{ mt: 1, textAlign: 'center' }}>
                  {t('worksheetCommon.invoiceNotApproved')}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
      <SelectMerchantDialog
        open={isMerchantDialogOpen}
        title={t('worksheetCommon.addMerchantPayment')}
        onConfirm={handleMerchantsSelected}
        onCancel={() => setIsMerchantDialogOpen(false)}
        allowMultiple
        financingProgramId={creditApplication!.financingProgramId}
        label={t('taskManager.merchantName')}
        financingCompanyId={selectedCompany}
      />
    </Grid>
  )
}

export default PaymentSplitSection
